import type { ColorParam, Generation, ImageModelId, LandscapingConfig } from '@paint-ai/shared';
import { useCallback, useMemo, useReducer, type ReactNode } from 'react';
import { useCreateMakeover } from '../hooks/useCreateMakeover';
import { StudioContext, type StudioContextValue } from './studio-context';
import { createInitialStudioState, studioReducer, type ColorSlot, type StudioState } from './studio-reducer';

/**
 * Owns the studio workspace. Mounted above the router so the workspace (and an
 * in-flight render) survives switching to the history page and back.
 */
export function StudioProvider({ children, initialState }: { children: ReactNode; initialState?: StudioState }) {
  const [state, dispatch] = useReducer(studioReducer, initialState ?? createInitialStudioState());
  const makeover = useCreateMakeover();
  const { mutate, reset: resetMakeover } = makeover;

  const actions = useMemo(
    () => ({
      selectImage: (image: string) => dispatch({ type: 'imageSelected', image }),
      setColor: (slot: ColorSlot, color: ColorParam) => dispatch({ type: 'colorChanged', slot, color }),
      setMasterPrompt: (masterPrompt: string) => dispatch({ type: 'masterPromptChanged', masterPrompt }),
      setModel: (model: ImageModelId) => dispatch({ type: 'modelChanged', model }),
      setLandscaping: (landscaping: LandscapingConfig) => dispatch({ type: 'landscapingChanged', landscaping }),
      loadGeneration: (generation: Generation) => {
        resetMakeover();
        dispatch({ type: 'generationLoaded', generation });
      },
      reset: () => {
        resetMakeover();
        dispatch({ type: 'reset' });
      },
    }),
    [resetMakeover],
  );

  const generate = useCallback(() => {
    const { image, colors, masterPrompt, landscaping, model } = state;
    mutate(
      { imageSrc: image, ...colors, masterPrompt, landscaping, model },
      { onSuccess: ({ generation }) => dispatch({ type: 'renderSucceeded', sourceImage: image, generation }) },
    );
  }, [state, mutate]);

  const pendingWall = makeover.isPending ? makeover.variables?.wall : undefined;
  const value = useMemo<StudioContextValue>(
    () => ({
      state,
      ...actions,
      generate,
      isGenerating: makeover.isPending,
      generationStatus: pendingWall
        ? `Wiping previous color scheme & applying 100% paint for ${pendingWall.name} (${pendingWall.hex})...`
        : null,
      generationError: makeover.error
        ? makeover.error.message || 'An error occurred while generating the architectural render.'
        : null,
    }),
    [state, actions, generate, makeover.isPending, makeover.error, pendingWall],
  );

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}
